import React from 'react';
import { Download, Monitor, Share, SquarePlus, X } from 'lucide-react';
import { Button } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import { useTranslation } from '../../i18n';
import {
  dismissInstallPrompt,
  getInstallState,
  promptInstall,
  subscribeInstallPrompt,
} from '../../lib/pwa';

/**
 * The install banner — the browser-mode invitation to become an app.
 *
 * The doctor panel's banner (doctor-app InstallPrompt.jsx), with one fork the
 * doctor app does not need: this panel is desktop-first, and its main install
 * target is a reception or accounting PC running Windows. There, Edge and
 * Chrome fire `beforeinstallprompt` exactly as Android does, and «نصب» opens
 * the real system dialog; the installed panel then gets its own window, a
 * taskbar and Start-menu entry, and the manifest's shortcuts as a jump list.
 *
 *   desktop (`isMobile` false)  a compact card in the bottom inline-end
 *                               corner — clear of the centred content and
 *                               the ShortcutProvider's `?` overlay.
 *   phone                       the doctor app's card above the bottom nav;
 *                               iOS gets the share-sheet walkthrough, because
 *                               Safari has no install API.
 *
 * Visibility is entirely `getInstallState()`'s call (installed window,
 * installed flag, cooldown, something-to-offer), so inside the installed app
 * — and in Firefox, which cannot install — this renders nothing.
 */
const InstallPrompt = ({ isMobile = false }) => {
  const { t } = useTranslation();
  const [state, setState] = React.useState(getInstallState);
  const [showIosSteps, setShowIosSteps] = React.useState(false);

  React.useEffect(
    () => subscribeInstallPrompt(() => setState(getInstallState())),
    [],
  );

  if (!state.visible) return null;

  const handleInstall = async () => {
    if (state.promptAvailable) {
      await promptInstall();
    } else {
      setShowIosSteps(true);
    }
  };

  const desktop = !isMobile && !state.ios;
  const iosSteps = [
    { Icon: Share, text: t('common.pwa.iosStep1') },
    { Icon: SquarePlus, text: t('common.pwa.iosStep2') },
    { Icon: Download, text: t('common.pwa.iosStep3') },
  ];
  const tile = {
    background: `linear-gradient(160deg, ${C.navyDark}, ${C.navyLight})`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  };
  const LeadIcon = desktop ? Monitor : Download;

  return (
    <div
      data-testid="pwa-install-prompt"
      data-variant={desktop ? 'desktop' : 'mobile'}
      role="dialog"
      aria-label={t(desktop ? 'common.pwa.desktopTitle' : 'common.pwa.installTitle')}
      style={{
        position: 'fixed',
        zIndex: 90,
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        boxShadow: '0 12px 40px rgba(0,0,0,0.16), 0 2px 10px rgba(0,0,0,0.08)',
        padding: 14,
        ...(desktop
          ? { insetInlineEnd: 24, bottom: 24, width: 380 }
          : {
            insetInline: 12,
            // Above the bottom nav: the kit's BottomNav publishes its height
            // as `--app-bottom-nav` while mounted.
            bottom: 'calc(12px + env(safe-area-inset-bottom, 0px) + var(--app-bottom-nav, 0px))',
            maxWidth: 440,
            marginInline: 'auto',
          }),
      }}
    >
      {!showIosSteps ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ ...tile, width: 46, height: 46, borderRadius: 12 }}>
            <LeadIcon size={22} color="#fff" aria-hidden />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: C.textPrimary }}>
              {t(desktop ? 'common.pwa.desktopTitle' : 'common.pwa.installTitle')}
            </div>
            <div style={{ fontSize: 11.5, color: C.textSecondary, lineHeight: 1.7, marginTop: 2 }}>
              {t(desktop ? 'common.pwa.desktopBody' : 'common.pwa.installBody')}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
            <Button data-testid="pwa-install-accept" size="sm" onClick={handleInstall}>
              {t('common.pwa.install')}
            </Button>
            <Button data-testid="pwa-install-dismiss" size="sm" variant="ghost" onClick={dismissInstallPrompt}>
              {t('common.pwa.later')}
            </Button>
          </div>
        </div>
      ) : (
        <div data-testid="pwa-ios-steps">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: C.textPrimary }}>
              {t('common.pwa.iosTitle')}
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={dismissInstallPrompt}
              aria-label={t('common.actions.close')}
            >
              <X size={15} aria-hidden />
            </Button>
          </div>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {iosSteps.map(({ Icon, text }, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ ...tile, width: 30, height: 30, borderRadius: 9 }}>
                  <Icon size={15} color="#fff" aria-hidden />
                </span>
                <span style={{ fontSize: 12.5, color: C.textSecondary, lineHeight: 1.7 }}>{text}</span>
              </li>
            ))}
          </ol>
          <Button data-testid="pwa-ios-got-it" onClick={dismissInstallPrompt} style={{ marginTop: 12, width: '100%' }}>
            {t('common.pwa.gotIt')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default InstallPrompt;
